export function propEvaluator(el: HTMLElement, ignoredScope: Record<string, any>, expression: string) {
  let dataStack = window.Alpine.closestDataStack(el).filter((obj) => obj !== ignoredScope)

  let evaluator = generateEvaluatorFromString(dataStack, expression, el)

  return tryCatch.bind(null, el, expression, evaluator)
}

// Taken from https://github.com/alpinejs/alpine/blob/main/packages/alpinejs/src/evaluator.js and stripped down
// this performs the same as alpine's normal evaluator, except that when it receives a function as a result it doesn't invoke it

function generateEvaluatorFromString(dataStack: object[], expression: string, el: HTMLElement) {
  let func = generateFunctionFromString(expression, el)

  return (receiver: (val: any) => unknown = (val: any) => {}, { scope = {} /*params = []*/ } = {}) => {
    func.result = undefined
    func.finished = false

    const completeScope: Record<string, any> = Alpine.mergeProxies([scope, ...dataStack])

    if (typeof func === 'function') {
      let promise = func(func, completeScope).catch((error: Error) => handleError(error, el, expression))

      // Check if the function ran synchronously,
      if (func.finished) {
        receiver(bindResult(func.result, completeScope))
        func.result = undefined
      } else {
        // If not, return the result when the promise resolves.
        promise
          .then((result: any) => bindResult(result, completeScope))
          .catch((error: Error) => handleError(error, el, expression))
          .finally(() => (func.result = undefined))
      }
    }
  }
}

function bindResult(result: any, scope: Record<string, any>) {
  if (result && typeof result === 'object') {
    return Alpine.reactive(result)
  }
  return typeof result === 'function' ? result.bind(scope) : result
}

const evaluatorMemo: Record<string, Function> = {}

function generateFunctionFromString(expression: string, el: HTMLElement) {
  if (evaluatorMemo[expression]) {
    return evaluatorMemo[expression]
  }

  let AsyncFunction = Object.getPrototypeOf(async function () {}).constructor

  // Some expressions that are useful in Alpine are not valid as the right side of an expression.
  // Here we'll detect if the expression isn't valid for an assignement and wrap it in a self-
  // calling function so that we don't throw an error AND a "return" statement can b e used.
  let rightSideSafeExpression =
    0 ||
    // Support expressions starting with "if" statements like: "if (...) doSomething()"
    /^[\n\s]*if.*\(.*\)/.test(expression) ||
    // Support expressions starting with "let/const" like: "let foo = 'bar'"
    /^(let|const)\s/.test(expression)
      ? `(() => { ${expression} })()`
      : expression

  const safeAsyncFunction = () => {
    try {
      return new AsyncFunction(
        ['__self', 'scope'],
        `with (scope) { __self.result = ${rightSideSafeExpression} }; __self.finished = true; return __self.result;`
      )
    } catch (error) {
      handleError(error as Error, el, expression)
      return Promise.resolve()
    }
  }
  let func = safeAsyncFunction()

  evaluatorMemo[expression] = func

  return func
}

// extracted from alpinejs/src/utils/error.js
function tryCatch(el: HTMLElement, expression: string, callback: Function, ...args: any[]) {
  try {
    return callback(...args)
  } catch (e) {
    handleError(e as Error, el, expression)
  }
}
function handleError(error: Error, el: HTMLElement, expression?: string) {
  Object.assign(error, { el, expression })
  console.warn(
    `Alpine Expression Error: ${error.message}

${expression ? 'Expression: "' + expression + '"\n\n' : ''}`,
    el
  )
  setTimeout(() => {
    throw error
  }, 0)
}
